import {format} from 'date-fns'

export interface QuoteMoney {
  readonly amount: number
  readonly currency: string
}

export interface ModifyQuoteResponse {
  readonly eligible: boolean
  readonly reason?: string
  readonly message?: string
  readonly kind?: string
  readonly pricingPath?: string
  readonly currentTotal?: QuoteMoney
  readonly newTotal?: QuoteMoney
  readonly delta?: QuoteMoney
  readonly newReservationTotal?: QuoteMoney
  readonly detail?: Record<string, unknown>
}

/**
 * The amount the guest is confirming — the new-reservation total for
 * prepaid extensions, the updated stay total otherwise
 */
export const confirmationTotal = (
  quote: ModifyQuoteResponse
): QuoteMoney | undefined => quote.newReservationTotal ?? quote.newTotal

export const formatMoney = (money?: QuoteMoney): string => {
  if (!money) {
    return '—'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: money.currency
  }).format(money.amount)
}

/** yyyy-MM-dd for the API from a locally-picked Date (timezone-agnostic) */
export const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd')

/** Guest-facing copy for each backend ineligibility reason */
export const ineligibleMessageFor = (reason?: string): string => {
  switch (reason) {
    case 'monthly-excluded':
    case 'monthly-target-excluded':
      return 'Changes to monthly stays are handled by our team — reach out and we’ll take care of it.'
    case 'prepaid-change-blocked':
      return 'This rate can’t be shortened or moved, but you can add nights. Pick a later check-out to see pricing.'
    case 'channel-not-allowed':
      return 'This reservation was booked through a partner site, so date changes happen there.'
    case 'corporate-excluded':
      return 'Corporate reservations are handled by our team — reach out and we’ll take care of it.'
    case 'cutoff-passed':
      return 'Same-day date changes close at 11 AM. Contact the property and we’ll help from there.'
    case 'in-house-arrival-shift-blocked':
      return 'Your stay has already started, so check-in can’t change — but check-out can.'
    case 'in-house-tier-switch-blocked':
      return 'That change would switch your rate type mid-stay. Contact the property and we’ll help from there.'
    case 'status-not-modifiable':
      return 'This reservation can’t be changed anymore.'
    case 'no-change':
      return 'Those are your current dates — pick a new check-in or check-out.'
    case 'invalid-dates':
      return 'Those dates don’t look right — check-out needs to come after check-in, and dates can’t be in the past.'
    default:
      return 'This change isn’t available online. Contact the property and we’ll help from there.'
  }
}

/** Guest-facing copy for apply-time API errors, keyed by response */
export const applyErrorMessageFor = (error: {
  status?: number
  data?: {error?: string}
}): string => {
  if (error?.data?.error === 'quote-changed') {
    return (
      'Pricing changed while you were confirming. Here’s the updated ' +
      'total — confirm again if it still works for you.'
    )
  }
  if (error?.data?.error === 'modify-in-progress') {
    return 'A change to this reservation is already being processed. Give it a moment and try again.'
  }
  if (error?.status === 403) {
    return 'Online date changes aren’t available for this property yet. Contact the property and we’ll take care of it.'
  }
  return 'We couldn’t update your reservation. Try again or contact the property.'
}

/** One line describing the price change, sign-aware */
export const deltaSummary = (quote: ModifyQuoteResponse): string => {
  if (quote.newReservationTotal) {
    return `Your current reservation stays as is — the added nights are booked separately for ${formatMoney(
      quote.newReservationTotal
    )}.`
  }
  const amount = quote.delta?.amount ?? 0
  if (amount > 0) {
    return `Your new total adds ${formatMoney(quote.delta)} to your stay.`
  }
  if (amount < 0) {
    return `Your new total is ${formatMoney({
      amount: Math.abs(amount),
      currency: quote.delta.currency
    })} less than before.`
  }
  return 'Your total stays the same.'
}
